<template>
  <v-dialog :value="value" @input="v => $emit('input', v)" max-width="1100px" scrollable>
    <v-card>
      <v-card-title class="pl-3 py-2" style="border-bottom:1px solid #ccc;">
        <span style="font-size:15px;font-weight:600;">HAE VEDOKSET</span>
        <v-spacer></v-spacer>
        <v-icon :size="14" style="cursor:pointer;" @click="$emit('input', false)">fas fa-times</v-icon>
      </v-card-title>

      <v-card-text class="pa-3" style="min-height:200px;">
        <!-- LOADING -->
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" :size="30"></v-progress-circular>
          <div class="mt-3" style="font-size:12px;color:#666;">
            Haetaan vedoksia tarjoukselle — Gmail-haku ja kuvien tunnistus voi kestää hetken…
          </div>
        </div>

        <!-- ERROR -->
        <div v-else-if="error" class="pa-3" style="font-size:13px;color:#c00;">
          {{ error }}
          <div class="mt-2">
            <button class="avecor-button-global small-add-button" @click="fetchDrafts" style="padding-bottom:10px !important;">YRITÄ UUDELLEEN</button>
          </div>
        </div>

        <!-- RESULT -->
        <div v-else-if="result">
          <div v-if="result.order" class="mb-2" style="font-size:11px;color:#666;">
            Tarjous {{ order.id }}
            <span v-if="result.order.netvisorId"> · tilaus {{ result.order.netvisorId }}</span>
            <span v-if="result.order.company"> · {{ result.order.company }}</span>
          </div>

          <!-- Product boxes — one per product that has drafts -->
          <v-row no-gutters v-if="groups.length">
            <v-col v-for="g in groups" :key="g.key" cols="12" sm="6" class="pa-1">
              <div style="border:1px solid #ccc;height:100%;">
                <div class="pa-1 pl-2" style="font-size:12px;font-weight:600;background:#eee;border-bottom:1px solid #ccc;">
                  {{ g.label }}
                </div>
                <div class="pa-1" style="display:flex;flex-wrap:wrap;">
                  <div v-for="a in g.items" :key="attKey(a)"
                    style="width:200px;margin:3px;border:1px solid #ddd;padding:3px;position:relative;">
                    <!-- confidence badge -->
                    <div v-if="a.confidence != null"
                      style="position:absolute;top:2px;right:2px;background:rgba(0,0,0,.7);color:#fff;font-size:9px;padding:1px 5px;border-radius:8px;z-index:1;">
                      {{ a.confidence }}%
                    </div>
                    <!-- preview -->
                    <div style="height:160px;display:flex;align-items:center;justify-content:center;background:#fafafa;overflow:hidden;cursor:pointer;"
                      @click="openPreview(a)">
                      <img v-if="previews[attKey(a)] && isImage(a)" :src="previews[attKey(a)]"
                        style="max-width:100%;max-height:160px;" />
                      <div v-else-if="isImage(a)" style="font-size:10px;color:#999;">ladataan…</div>
                      <pdf v-else-if="isPdf(a) && previews[attKey(a)]" :src="previews[attKey(a)]"
                        :page="1" style="width:100%;"></pdf>
                      <div v-else class="text-center" style="color:#555;">
                        <v-icon :size="28">far fa-file-pdf</v-icon>
                        <div style="font-size:9px;">PDF</div>
                      </div>
                    </div>
                    <div style="font-size:9px;word-break:break-all;line-height:11px;padding-top:3px;">
                      {{ a.filename }}
                    </div>
                    <div style="font-size:9px;color:#666;line-height:11px;">
                      <div>{{ a.subject }}</div>
                      <div>{{ formatDate(a.date) }}</div>
                    </div>
                    <!-- set draft -->
                    <div class="mt-1 text-center">
                      <div v-if="uploads[attKey(a)] && uploads[attKey(a)].done"
                        style="font-size:10px;color:green;">
                        Lisätty ✓ {{ uploads[attKey(a)].fileId }}
                      </div>
                      <div v-else-if="uploads[attKey(a)] && uploads[attKey(a)].duplicate"
                        style="font-size:10px;color:#a60;">
                        Jo lisätty · {{ uploads[attKey(a)].fileId }}
                      </div>
                      <div v-else-if="uploads[attKey(a)] && uploads[attKey(a)].error"
                        style="font-size:10px;color:#c00;">
                        {{ uploads[attKey(a)].error }}
                      </div>
                      <button v-else-if="uploads[attKey(a)] && uploads[attKey(a)].processing"
                        class="avecor-button-global small-add-button" disabled
                        style="width:100%;font-size:10px;padding-bottom:10px !important;">
                        KÄSITELLÄÄN…
                      </button>
                      <div v-else-if="uploads[attKey(a)]" style="width:100%;">
                        <v-progress-linear :value="uploads[attKey(a)].pct" height="6" color="primary"></v-progress-linear>
                        <div style="font-size:9px;color:#666;">Lähetetään… {{ uploads[attKey(a)].pct }}%</div>
                      </div>
                      <button v-else class="avecor-button-global small-add-button"
                        :disabled="!a.productId || !a.productVariationId"
                        @click="setDraft(a)"
                        style="width:100%;font-size:10px;padding-bottom:10px !important;">
                        ASETA VEDOS
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <div v-else class="pa-3" style="font-size:13px;color:#666;">
            Vedoksia ei löytynyt.
          </div>

          <!-- products with no draft -->
          <div v-if="result.noDraftFound && result.noDraftFound.length" class="mt-2" style="font-size:11px;color:#a60;">
            Ei vedosta: {{ result.noDraftFound.join(', ') }}
          </div>

          <!-- raw hits for manual Gmail lookup -->
          <div v-if="result.results && result.results.length" class="mt-3 pt-2" style="border-top:1px dotted #ccc;font-size:10px;color:#888;">
            <div style="font-weight:600;">Löydetyt sähköpostit:</div>
            <div v-for="(m, i) in result.results" :key="i">
              {{ formatDate(m.date) }} · {{ m.from }} · "{{ m.subject }}" — {{ (m.files || m.attachments || []).map(f => f.filename).join(', ') }}
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- PDF viewer -->
    <v-dialog v-model="pdfOpen" max-width="800px">
      <v-card>
        <v-card-text class="pa-2">
          <div class="text-center mb-1" style="font-size:12px;">
            <v-icon v-if="pdfPage > 1" @click="pdfPage -= 1" size="18">far fa-arrow-alt-circle-left</v-icon>
            <v-icon v-else size="18" style="color:#aaa">far fa-arrow-alt-circle-left</v-icon>
            <v-icon v-if="pdfPage < pdfNumPages" @click="pdfPage += 1" size="18" style="margin-left:6px">far fa-arrow-alt-circle-right</v-icon>
            <v-icon v-else size="18" style="margin-left:6px;color:#aaa">far fa-arrow-alt-circle-right</v-icon>
            <span style="margin-left:10px;">{{ pdfPage }} / {{ pdfNumPages }}</span>
          </div>
          <pdf v-if="pdfUrl" style="width:100%" :page="pdfPage" :src="pdfUrl"
            @num-pages="pdfNumPages = $event"></pdf>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- image viewer -->
    <v-dialog v-model="imgOpen" max-width="800px">
      <v-card>
        <v-card-text class="pa-2 text-center">
          <img v-if="imgUrl" :src="imgUrl" style="max-width:100%;" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import firebase from 'firebase'
import axios from 'axios'
import pdf from 'vue-pdf'

export default {
  name: 'FetchDraftsModal',
  components: { pdf },
  props: ['value', 'order'],
  data: () => ({
    loading: false,
    error: '',
    result: null,
    blobs: {},     // attKey -> Blob (preview source + upload payload)
    previews: {},  // attKey -> objectURL (images + PDFs)
    uploads: {},   // attKey -> {pct, processing, done, fileId, error}
    pdfOpen: false,
    pdfUrl: null,
    pdfPage: 1,
    pdfNumPages: 0,
    imgOpen: false,
    imgUrl: null,
  }),
  computed: {
    ...mapFields(['settings']),
    apiBase() {
      return (this.settings && this.settings.dev)
        ? 'http://0.0.0.0:5000/logogear-erp/us-central1'
        : 'https://us-central1-logogear-erp.cloudfunctions.net'
    },
    groups() {
      const atts = (this.result && this.result.data && this.result.data.attachments) || []
      const map = new Map()
      for (const a of atts) {
        const key = a.orderedProductId || a.productVariationId || a.productId || 'other'
        const label = a.productName
          ? `${a.productName}${a.variationName ? ' / ' + a.variationName : ''}`
          : 'Tunnistamaton'
        if (!map.has(key)) map.set(key, { key, label, items: [] })
        map.get(key).items.push(a)
      }
      return [...map.values()]
    },
  },
  watch: {
    value(v) {
      if (v) {
        this.result = null
        this.error = ''
        this.previews = {}
        this.blobs = {}
        this.uploads = {}
        this.fetchDrafts()
      }
    },
  },
  methods: {
    attKey(a) { return `${a.messageId}:${a.attachmentId}` },
    isImage(a) { return (a.mimeType || '').startsWith('image/') || /\.(jpe?g|png|gif|webp)$/i.test(a.filename || '') },
    isPdf(a) { return a.mimeType === 'application/pdf' || /\.pdf$/i.test(a.filename || '') },
    formatDate(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    },
    async token() {
      return await firebase.auth().currentUser.getIdToken()
    },
    attUrl(a) {
      return `${this.apiBase}/offerDrafts?offerId=${encodeURIComponent(this.order.id)}` +
        `&att=${encodeURIComponent(this.attKey(a))}` +
        `&name=${encodeURIComponent(a.filename || '')}&mime=${encodeURIComponent(a.mimeType || '')}`
    },
    async fetchDrafts() {
      this.loading = true
      try {
        const res = await axios.get(
          `${this.apiBase}/offerDrafts?offerId=${encodeURIComponent(this.order.id)}`,
          { headers: { Authorization: `Bearer ${await this.token()}` } })
        this.result = res.data
        this.loading = false
        // Eagerly fetch bytes — same blobs feed previews and the upload.
        const atts = (res.data.data && res.data.data.attachments) || []
        atts.forEach(a => this.loadBlob(a))
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.error) || e.message
        this.loading = false
      }
    },
    async loadBlob(a) {
      const k = this.attKey(a)
      try {
        const res = await axios.get(this.attUrl(a), {
          headers: { Authorization: `Bearer ${await this.token()}` },
          responseType: 'blob',
        })
        this.$set(this.blobs, k, res.data)
        if (this.isImage(a) || this.isPdf(a)) this.$set(this.previews, k, URL.createObjectURL(res.data))
      } catch (e) { /* preview best-effort */ }
    },
    async openPreview(a) {
      const k = this.attKey(a)
      if (!this.blobs[k]) await this.loadBlob(a)
      if (!this.blobs[k]) return
      if (this.isPdf(a)) {
        this.pdfUrl = this.previews[k] || URL.createObjectURL(this.blobs[k])
        this.pdfPage = 1
        this.pdfOpen = true
      } else {
        this.imgUrl = this.previews[k] || URL.createObjectURL(this.blobs[k])
        this.imgOpen = true
      }
    },
    async setDraft(a) {
      const k = this.attKey(a)
      this.$set(this.uploads, k, { pct: 0 })
      try {
        // Preflight duplicate check — same original filename + company already
        // exists as a draft → skip the upload entirely (server re-checks too).
        const cid = a.companyId || this.order.companyId || ''
        const nm = (a.filename || '').split('.').slice(0, -1).join(',')
        if (cid && nm) {
          const dup = await firebase.firestore().collection('files')
            .where('type', '==', 'draft')
            .where('companyId', '==', cid)
            .where('name', '==', nm)
            .limit(1).get()
          if (!dup.empty) {
            this.$set(this.uploads, k, { duplicate: true, fileId: dup.docs[0].id })
            return
          }
        }
        if (!this.blobs[k]) await this.loadBlob(a)
        if (!this.blobs[k]) throw new Error('Tiedostoa ei saatu ladattua')
        const fd = new FormData()
        fd.append('file', new File([this.blobs[k]], a.filename || 'draft', { type: a.mimeType || 'application/octet-stream' }))
        const headers = {
          Authorization: `Bearer ${await this.token()}`,
          type: 'draft',
          companyid: a.companyId || this.order.companyId || '',
          orderid: this.order.id, // offerId
          productid: a.productId || '',
          productvariationid: a.productVariationId || '',
          orderedproductid: a.orderedProductId || '',
        }
        const res = await axios.post(`${this.apiBase}/uploadFile`, fd, {
          headers,
          onUploadProgress: ev => {
            if (ev.total) this.$set(this.uploads, k, { pct: Math.round(ev.loaded / ev.total * 100) })
          },
        })
        // Bytes sent — server still converts/resizes before responding.
        this.$set(this.uploads, k, { pct: 100, processing: true })
        const fileId = res.data && (res.data.fileId || res.data.id) ? (res.data.fileId || res.data.id) : ''
        this.$set(this.uploads, k, { done: true, fileId })
      } catch (e) {
        if (e.response && e.response.status === 409 && e.response.data && e.response.data.error === 'duplicate') {
          this.$set(this.uploads, k, { duplicate: true, fileId: e.response.data.existingFileId || '' })
          return
        }
        const msg = (e.response && e.response.data && (e.response.data.error || e.response.data.message)) || e.message
        this.$set(this.uploads, k, { error: String(msg) })
      }
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/blocks/FetchDraftsModal.vue